import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router';

import { useProjectApi } from 'src/services';
import { useFetchStatus } from 'src/hooks';
import { TitleContainer, ContentContainer } from 'src/components';
import { WebContext } from 'src/scenes/web-context';
import { getLeafletOptionsFn } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { AnalyticCard, SpatialFootprintTable } from 'src/scenes/Project/components';
import { AspectCategoryStatusIcon } from 'src/scenes/Project/scenes/PressuresAndDependencies/components';
import { getHighestImpactCategory } from 'src/utils/aspectCategoriesUtils';
import { objectIsEmpty } from 'src/utils/util';
import { GeospatialAnalytic, LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';


const pressureCategoriesLang = {
  areaOfLandUse: 'Cambio de uso de suelo',
  areaOfFreshwaterUse: 'Uso de ecosistemas de agua dulce',
  areaOfSeabedUse: 'Uso de ecosistemas marinos',
  volumeOfWaterUse: 'Uso de agua dulce',
  otherBioticResourceExtraction: 'Uso de otros recursos bióticos',
  otherAbioticResourceExtraction: 'Uso de otros recursos abióticos',
  emissionsOfGhg: 'Emisión de GEI',
  emissionsOfNonGhgAirPollutants: 'Contaminación con gases no GEI',
  emissionsOfToxicSoilAndWaterPollutants: 'Emisión de contaminantes tóxicos en agua y suelos',
  emissionsOfNutrientSoilAndWaterPollutants: 'Emisión de nutrientes contaminantes en agua y suelos',
  generationAndReleaseOfSolidWaste: 'Eliminación de desechos sólidos',
  introductionOfInvasiveSpecies: 'Introducción de especies invasoras',
  disturbances: 'Perturbaciones (ruido, luz, entre otras)',
};

const pressureGroups = [
  {
    id: 'landEcoUse',
    label: 'Uso de suelo y ecosistemas',
    categories: [ 'areaOfLandUse', 'areaOfFreshwaterUse', 'areaOfSeabedUse' ],
  },
  {
    id: 'resourceUse',
    label: 'Explotación de recursos',
    categories: [ 'volumeOfWaterUse', 'otherBioticResourceExtraction', 'otherAbioticResourceExtraction' ],
  },
  {
    id: 'climateChange',
    label: 'Cambio climático',
    categories: [ 'emissionsOfGhg' ],
  },
  {
    id: 'pollution',
    label: 'Contaminación',
    categories: [
      'emissionsOfNonGhgAirPollutants',
      'emissionsOfToxicSoilAndWaterPollutants',
      'emissionsOfNutrientSoilAndWaterPollutants',
      'generationAndReleaseOfSolidWaste',
    ],
  },
  {
    id: 'invasiveSpecies',
    label: 'Especies invasoras y otras perturbaciones',
    categories: [ 'introductionOfInvasiveSpecies', 'disturbances' ],
  },
];

const useStyles = makeStyles(theme => ({
  grid: {
    height: '100%',
  },
  group: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray,
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
}));

const PressuresContainer = ({ interestArea, project }) => {
  const classes = useStyles();
  const webContext = useContext(WebContext);
  const { token } = webContext;

  const match = useRouteMatch();
  const projectApi = useProjectApi();

  const [ isicPressures, setIsicPressures ] = useState({});
  const [ spatialFootprint, setSpatialFootprint ] = useState({});

  const [ geospatialAnalytics, setGeospatialAnalytics ] = useState([]);

  const [ geospatialOptions, setGeospatialOptions ] = useState({
    conversionAreasOptions: [],
  });

  const {
    handleConfirm: fetchSpatialFootprint,
    isLoading: isLoadingSpatialFootprint,
    isSuccess: isSuccessSpatialFootprint,
  } = useFetchStatus({
    confirmFn: async () => {
      const spatialFootprint = await projectApi.getSpatialFootprint(projectId);
      setSpatialFootprint(spatialFootprint);
    },
  });

  const { projectId } = match.params;

  const {
    handleConfirm: fetchGeospatialAnalytics,
    isLoading: isLoadingGeospatialAnalytics,
    isSuccess: isSuccessGeospatialAnalytics,
  } = useFetchStatus({
    confirmFn: async () => {
      const [ geospatialAnalytics, geospatialOptions ] = await Promise.all([
        projectApi.getGeospatialAnalytics({ projectId, componentId: 'pressure' }),
        projectApi.getGeospatialOptions({ projectId, componentId: 'pressure' }),
      ]);

      setGeospatialOptions(geospatialOptions);
      setGeospatialAnalytics(geospatialAnalytics);
    },
  });

  useEffect(() => {
    fetchGeospatialAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIsicPressures = async () => {
    try {
      const { pressures } = await projectApi.getPressuresAndDependencies(projectId);
      setIsicPressures(pressures);
    } catch (error) {
      console.error('Error fetching ISIC pressures:', error);
    }
  };

  useEffect(() => {
    fetchIsicPressures();
    fetchSpatialFootprint();
  }, [ projectId ]); // eslint-disable-line react-hooks/exhaustive-deps

  const PressureCategory = pressureGroup => {
    if (objectIsEmpty(isicPressures)) {
      return null;
    }
    const groupValues = pressureGroup.categories.map(category => isicPressures[category]);
    const highest = getHighestImpactCategory(groupValues);
    return (
      <Box mt={-1}>
        <AspectCategoryStatusIcon category={highest} />
      </Box>
    );
  };

  const pressuresAnalytic = useMemo(() =>
    geospatialAnalytics.find(g => [ 'conversion-areas' ].includes(g.geoInfoTypeId)),
  [ geospatialAnalytics ],
  );

  const hasSpatialFootprint = !objectIsEmpty(spatialFootprint);

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        project={project}
        breadcrumbs={[ 'projects', 'project' ]}>
        Presiones
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <AnalyticCard title='Screening'>
          <Divider />
          <Box mt={2} />
          <Grid container spacing={1} >
            {pressureGroups.map(pressureGroup =>
              <Grid key={pressureGroup.id} item xs={4} className={classes.grid}>
                <Box className={classes.group}>
                  <Box display="flex" mb={0.5} alignItems="center">
                    <Typography variant="body2" className={classes.subtitle}>{pressureGroup.label}</Typography>
                    <Box flexGrow={1} />
                    {PressureCategory(pressureGroup)}
                  </Box>
                  <Divider />
                  <Box mt={1} />
                  {pressureGroup.categories.map(category =>
                    <Box key={category} display="flex" alignItems="center" py={0.5} mr={0.5}>
                      <Typography variant="body2" color="textPrimary">
                        {pressureCategoriesLang[category]}
                      </Typography>
                      <Box flexGrow={1} />
                      <AspectCategoryStatusIcon category={isicPressures[category]} size="small" />
                    </Box>,
                  )}
                </Box>
              </Grid>,
            )}
          </Grid>
        </AnalyticCard>
        <Box my={1} />
        <Grid container spacing={1}>
          <GeospatialAnalytic
            title="Indicadores"
            analytic={pressuresAnalytic}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            geospatialTypesOptions={geospatialOptions.conversionAreasOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
            isUnique
          />
          {(isLoadingSpatialFootprint || hasSpatialFootprint) &&
            <Grid item xs={12}>
              <AnalyticCard title="Huella espacial total">
                <Divider />
                <Box mt={2} />
                {isLoadingSpatialFootprint && <LoadingBox label="huella espacial" />}
                {isSuccessSpatialFootprint && <SpatialFootprintTable data={spatialFootprint} /> }
              </AnalyticCard>
            </Grid>
          }
        </Grid>
        <br />
      </ContentContainer>
    </Grid>
  );
};

PressuresContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
  interestArea: PropTypes.shape({
    data: PropTypes.array.isRequired,
    fetchStatus: PropTypes.object,
  }).isRequired,
};


export {
  PressuresContainer,
};
